import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Avatar, PhoneNumber } from "@vd/ui";
import { Headphones } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { liveQuery } from "../../api/team";
import type { LiveCall, LiveSegmentEvent, Segment } from "../../api/types";
import { liveSocket } from "../../api/ws";
import { requireManager } from "../../auth/useMe";
import { CallViews } from "../../components/CallViews";
import { Card, cx, Dialog, Dot, EmptyState, ErrorState, Loading, PageHeader } from "../../components/ui";
import { callHistorySearch } from "../../lib/callHistorySearch";
import { fmtDuration, fmtPhone } from "../../lib/format";
import {
  applyCall,
  applySegment,
  dropEnded,
  type LiveEntry,
  lastLines,
  monitorLine,
  noAudioCaption,
} from "../../lib/liveNow";

export const Route = createFileRoute("/admin/live-now")({
  staticData: { crumb: { label: "Live now", parents: [] } },
  beforeLoad: requireManager,
  validateSearch: (search) => callHistorySearch.parse(search),
  component: LiveNowPage,
});

function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/** The team's live calls: the first load, then `live.*` frames while the page is open. */
export function useLiveCalls() {
  const q = useQuery({ ...liveQuery, staleTime: 0, gcTime: 0 });
  const [calls, setCalls] = useState<LiveEntry[]>([]);
  useEffect(() => {
    if (q.data) {
      const now = Date.now();
      setCalls(q.data.calls.reduce<LiveEntry[]>((l, c) => applyCall(l, c, now), []));
    }
  }, [q.data]);
  useEffect(() => {
    const unsub = liveSocket.subscribe("live");
    const off = liveSocket.onFrame((f) => {
      if (f.type === "live.call") setCalls((l) => applyCall(l, f.data as LiveCall, Date.now()));
      else if (f.type === "live.segment") setCalls((l) => applySegment(l, f.data as LiveSegmentEvent));
    });
    const sweep = setInterval(() => setCalls((l) => dropEnded(l, Date.now())), 1000);
    return () => {
      off();
      unsub();
      clearInterval(sweep);
    };
  }, []);
  return { q, calls };
}

function LiveNowPage() {
  const search = Route.useSearch();
  const { q, calls } = useLiveCalls();
  const now = useNow();
  const [open, setOpen] = useState<string | null>(null);
  const opened = calls.find((c) => c.call_id === open) ?? null;
  return (
    <>
      <PageHeader title="Calls" />
      <CallViews search={search} />
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : calls.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Headphones />}
            title="No one is on a call"
            description="Calls show up here the moment they start."
          />
        </Card>
      ) : (
        <ul className="grid gap-3 min-[1100px]:grid-cols-2">
          {calls.map((c) => (
            <li key={c.call_id} className="animate-vd-line-in">
              <LiveCard call={c} now={now} onOpen={() => setOpen(c.call_id)} />
            </li>
          ))}
        </ul>
      )}
      {opened ? (
        <Dialog
          open
          onClose={() => setOpen(null)}
          size="lg"
          title={`${opened.rep.name} with ${opened.contact_name ?? fmtPhone(opened.phone)}`}
        >
          <div className="flex flex-col gap-3">
            <CallFacts call={opened} now={now} />
            <Transcript lines={opened.transcript} repName={opened.rep.name} tall />
            <Caption call={opened} />
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

function statusOf(c: LiveCall): { label: string; tone: "accent" | "ok" | "neutral" } {
  if (c.state === "ended") return { label: "Ended", tone: "neutral" };
  if (c.state === "ringing") return { label: "Ringing", tone: "accent" };
  return { label: "Live", tone: "ok" };
}

function CallFacts({ call, now }: { call: LiveCall; now: number }) {
  const s = statusOf(call);
  const from = Date.parse(call.answered_at ?? call.started_at);
  return (
    <div className="flex items-center gap-3">
      <Avatar name={call.rep.name} userId={call.rep.id} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ui">{call.rep.name}</p>
        <p className="truncate text-caption text-ink-2">
          {call.contact_name ? `${call.contact_name}, ` : ""}
          <PhoneNumber e164={call.phone} types={call.phone_types} focusable={false} />
          {call.direction === "inbound" ? " (inbound)" : ""}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className="inline-flex items-center gap-1.5 text-caption">
          <Dot tone={s.tone} />
          {s.label}
        </span>
        <span className="tnum text-caption text-ink-2">
          {fmtDuration(Math.max(0, Math.round((now - from) / 1000)))}
        </span>
      </div>
    </div>
  );
}

function Caption({ call }: { call: LiveCall }) {
  const why = noAudioCaption(call);
  return (
    <div className="flex flex-col gap-1">
      {call.monitors.map((m) => (
        <p key={m.participant_id} className="flex items-center gap-1.5 text-caption text-ink-2">
          <Dot tone="ok" />
          {monitorLine(m.name, m.mode)}
        </p>
      ))}
      <p className="text-caption text-ink-3">{why ?? "Listen from the desktop app."}</p>
    </div>
  );
}

function Transcript({ lines, repName, tall }: { lines: Segment[]; repName: string; tall?: boolean }) {
  const shown = useMemo(() => (tall ? lines : lastLines(lines)), [lines, tall]);
  if (shown.length === 0)
    return <p className="rounded-lg bg-well px-3 py-2 text-caption text-ink-3">Waiting for words</p>;
  return (
    <ol
      aria-label="Live transcript"
      className={cx(
        "flex flex-col gap-1.5 rounded-lg bg-well px-3 py-2",
        tall && "max-h-[420px] overflow-y-auto",
      )}
    >
      {shown.map((s) => (
        <li key={`${s.speaker}-${s.start_ms}`} className="text-ui">
          <span
            className={cx(
              "mr-2 font-medium text-caption",
              s.speaker === "rep" ? "text-accent-ink" : "text-ink",
            )}
          >
            {s.speaker === "rep" ? (repName.split(" ")[0] ?? "Rep") : "Customer"}
          </span>
          <span className={cx(!s.is_final && "text-ink-2")}>{s.text}</span>
        </li>
      ))}
    </ol>
  );
}

function LiveCard({ call, now, onOpen }: { call: LiveEntry; now: number; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open ${call.rep.name}'s call`}
      className={cx(
        "press flex w-full flex-col gap-3 rounded-2xl bg-surface-1 p-4 text-left shadow-[0_0_0_1px_var(--vd-hairline)] hover:bg-surface-2",
        call.state === "ended" && "opacity-60",
      )}
    >
      <CallFacts call={call} now={now} />
      <Transcript lines={call.transcript} repName={call.rep.name} />
      <Caption call={call} />
    </button>
  );
}
